import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Clock, MapPin, Camera, AlertTriangle, Car, Users, X, LayoutGrid, GanttChart, Grip, Navigation, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = [
  { bg: 'bg-blue-500', text: 'text-blue-700', light: 'bg-blue-50', border: 'border-blue-200' },
  { bg: 'bg-emerald-500', text: 'text-emerald-700', light: 'bg-emerald-50', border: 'border-emerald-200' },
  { bg: 'bg-purple-500', text: 'text-purple-700', light: 'bg-purple-50', border: 'border-purple-200' },
  { bg: 'bg-amber-500', text: 'text-amber-700', light: 'bg-amber-50', border: 'border-amber-200' },
  { bg: 'bg-rose-500', text: 'text-rose-700', light: 'bg-rose-50', border: 'border-rose-200' },
  { bg: 'bg-cyan-500', text: 'text-cyan-700', light: 'bg-cyan-50', border: 'border-cyan-200' },
  { bg: 'bg-indigo-500', text: 'text-indigo-700', light: 'bg-indigo-50', border: 'border-indigo-200' },
  { bg: 'bg-teal-500', text: 'text-teal-700', light: 'bg-teal-50', border: 'border-teal-200' },
];

export default function AssignmentManager({ eventId, onNavigateToSpot, onEditSpot }) {
  const [spots, setSpots] = useState([]);
  const [photographers, setPhotographers] = useState([]);
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('timeline');
  const [editingTime, setEditingTime] = useState(null); // {spotId, photographerId, arrive_time, leave_time}

  useEffect(() => {
    if (eventId) fetchData();
  }, [eventId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const [spotsRes, photographersRes] = await Promise.all([
        axios.get(`${API}/spots?event_id=${eventId}`, config),
        axios.get(`${API}/events/${eventId}/photographers`, config)
      ]);
      setSpots(spotsRes.data);
      setPhotographers(photographersRes.data);
    } catch (error) {
      toast.error('Failed to load data: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const assignPhotographer = async (spotId, photographerId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/spots/${spotId}/assign`,
        { photographer_id: photographerId, spot_id: spotId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Photographer assigned!');
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to assign';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  };

  const unassignPhotographer = async (spotId, photographerId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/spots/${spotId}/unassign/${photographerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Photographer unassigned!');
      fetchData();
    } catch (error) {
      toast.error('Failed to unassign photographer');
    }
  };

  const updatePhotographerTimes = async (spotId, photographerId, arriveTime, leaveTime) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/spots/${spotId}/photographer-times/${photographerId}`,
        { arrive_time: arriveTime || null, leave_time: leaveTime || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Times updated');
      setEditingTime(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update times');
    }
  };

  // Get photographer's time for a specific spot (per-photographer or spot default).
  // Coverage virtual spots already have arrive/leave_time = coverage window,
  // so just return them directly.
  const getPhotographerTime = (spot, photographerId) => {
    if (spot.is_coverage) {
      return { arrive_time: spot.arrive_time, leave_time: spot.leave_time };
    }
    const pt = spot.photographer_times?.[photographerId];
    return {
      arrive_time: pt?.arrive_time || spot.arrive_time,
      leave_time: pt?.leave_time || spot.leave_time
    };
  };

  const calculateTravelTime = (spot1, spot2) => {
    if (!spot1.lat || !spot1.lng || !spot2.lat || !spot2.lng) return null;
    const R = 6371;
    const dLat = (spot2.lat - spot1.lat) * Math.PI / 180;
    const dLon = (spot2.lng - spot1.lng) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(spot1.lat * Math.PI / 180) * Math.cos(spot2.lat * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    const travelTime = distance < 1 ? Math.ceil(distance * 10) : Math.ceil(distance * 4 + 10);
    return { distance: distance.toFixed(2), travelTime, method: distance < 1 ? 'Walking' : 'Car' };
  };

  const getPhotographerAssignments = (photographerId) => {
    // Native assignments: photographer is in assigned_photographers
    const native = spots.filter(spot => spot.assigned_photographers?.includes(photographerId));

    // Coverage assignments: photographer covers a break on this spot. Each
    // coverage entry becomes a virtual spot with the coverage time window so
    // it shows up in the timeline (and tooltip explains it's a coverage).
    const coverage = [];
    for (const spot of spots) {
      const cov = spot.break_coverage || [];
      for (const c of cov) {
        if (c.covering_photographer_id !== photographerId) continue;
        coverage.push({
          ...spot,
          id: `${spot.id}::cov::${c.id}`,
          arrive_time: c.start_time,
          leave_time: c.end_time,
          is_coverage: true,
          coverage_for_photographer_id: c.covers_for_id,
          coverage_note: c.note,
          original_spot_id: spot.id,
          // Strip per-photographer overrides — the coverage window is the truth
          photographer_times: {},
        });
      }
    }

    return [...native, ...coverage].sort((a, b) => {
      const aTime = (a.is_coverage ? a.arrive_time : getPhotographerTime(a, photographerId).arrive_time) || '';
      const bTime = (b.is_coverage ? b.arrive_time : getPhotographerTime(b, photographerId).arrive_time) || '';
      return aTime.localeCompare(bTime);
    });
  };

  const calculateTotalHours = (assignments, photographerId) => {
    let totalMinutes = 0;
    assignments.forEach(spot => {
      const times = getPhotographerTime(spot, photographerId);
      if (times.arrive_time && times.leave_time) {
        const [aH, aM] = times.arrive_time.split(':').map(Number);
        const [lH, lM] = times.leave_time.split(':').map(Number);
        totalMinutes += (lH * 60 + lM) - (aH * 60 + aM);
      }
    });
    return (totalMinutes / 60).toFixed(1);
  };

  const detectTimingConflict = (assignments, photographerId) => {
    const conflicts = [];
    for (let i = 0; i < assignments.length - 1; i++) {
      const current = assignments[i];
      const next = assignments[i + 1];
      const currentTimes = getPhotographerTime(current, photographerId);
      const nextTimes = getPhotographerTime(next, photographerId);
      if (!currentTimes.leave_time || !nextTimes.arrive_time) continue;
      const travel = calculateTravelTime(current, next);
      if (!travel) continue;
      const [lH, lM] = currentTimes.leave_time.split(':').map(Number);
      const [aH, aM] = nextTimes.arrive_time.split(':').map(Number);
      const gap = (aH * 60 + aM) - (lH * 60 + lM);
      if (gap < travel.travelTime) {
        conflicts.push({ from: current.name, to: next.name, required: travel.travelTime, actual: gap });
      }
    }
    return conflicts;
  };

  const timeToMinutes = (timeStr) => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
  };

  const eventTimeRange = useMemo(() => {
    let earliest = 24 * 60, latest = 0, hasTime = false;
    spots.forEach(spot => {
      // Check all photographer times + spot defaults
      const allTimes = [spot.arrive_time, spot.leave_time];
      if (spot.photographer_times) {
        Object.values(spot.photographer_times).forEach(pt => {
          if (pt.arrive_time) allTimes.push(pt.arrive_time);
          if (pt.leave_time) allTimes.push(pt.leave_time);
        });
      }
      // Include break_coverage windows so the timeline scale covers them.
      (spot.break_coverage || []).forEach((c) => {
        if (c.start_time) allTimes.push(c.start_time);
        if (c.end_time) allTimes.push(c.end_time);
      });
      allTimes.forEach(t => {
        if (t) {
          const m = timeToMinutes(t);
          if (m !== null) { earliest = Math.min(earliest, m); latest = Math.max(latest, m); hasTime = true; }
        }
      });
    });
    if (!hasTime) return { startMinutes: 360, endMinutes: 1080, totalMinutes: 720 };
    earliest = Math.max(0, earliest - 30);
    latest = Math.min(1440, latest + 30);
    return { startMinutes: earliest, endMinutes: latest, totalMinutes: latest - earliest };
  }, [spots]);

  const minuteToPercent = (minutes) => ((minutes - eventTimeRange.startMinutes) / eventTimeRange.totalMinutes) * 100;

  const stats = useMemo(() => {
    const assigned = spots.filter(s => s.assigned_photographers?.length > 0).length;
    const activePhotographers = photographers.filter(p => getPhotographerAssignments(p.id).length > 0).length;
    let totalConflicts = 0;
    photographers.forEach(p => { totalConflicts += detectTimingConflict(getPhotographerAssignments(p.id), p.id).length; });
    return { assigned, unassigned: spots.length - assigned, activePhotographers, idle: photographers.length - activePhotographers, totalConflicts, coverage: spots.length > 0 ? Math.round((assigned / spots.length) * 100) : 0 };
  }, [spots, photographers]);

  const unassignedSpots = useMemo(() => spots.filter(s => !s.assigned_photographers?.length), [spots]);

  const hourMarkers = useMemo(() => {
    const markers = [];
    const startH = Math.floor(eventTimeRange.startMinutes / 60);
    const endH = Math.ceil(eventTimeRange.endMinutes / 60);
    for (let h = startH; h <= endH; h++) {
      const m = h * 60;
      if (m >= eventTimeRange.startMinutes && m <= eventTimeRange.endMinutes) {
        markers.push({ time: `${h.toString().padStart(2, '0')}:00`, percent: minuteToPercent(m) });
      }
    }
    return markers;
  }, [eventTimeRange]);

  const handleSpotDragStart = (e, spot) => { e.dataTransfer.setData('spot', JSON.stringify(spot)); };
  const handlePhotographerDrop = async (e, photographerId) => {
    e.preventDefault(); e.currentTarget.classList.remove('ring-2', 'ring-blue-500', 'bg-blue-50');
    const d = e.dataTransfer.getData('spot');
    if (d) await assignPhotographer(JSON.parse(d).id, photographerId);
  };
  const handleDragOver = (e) => { e.preventDefault(); e.currentTarget.classList.add('ring-2', 'ring-blue-500', 'bg-blue-50'); };
  const handleDragLeave = (e) => { e.currentTarget.classList.remove('ring-2', 'ring-blue-500', 'bg-blue-50'); };

  if (loading) {
    return <div className="h-full flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" /></div>;
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Assignment Manager</h2>
            <p className="text-xs text-gray-500">Click any spot name to view on map</p>
          </div>
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
            <Button variant={viewMode === 'timeline' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('timeline')} className="gap-1.5">
              <GanttChart className="w-4 h-4" /> Timeline
            </Button>
            <Button variant={viewMode === 'grid' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('grid')} className="gap-1.5">
              <LayoutGrid className="w-4 h-4" /> Grid
            </Button>
          </div>
        </div>
        {/* Stats */}
        <div className="flex gap-3 text-xs flex-wrap">
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-50 rounded-md">
            <MapPin className="w-3.5 h-3.5 text-gray-500" />
            <span className="font-medium">{spots.length} spots</span>
            <span className="text-gray-400">|</span>
            <span className="text-emerald-600">{stats.assigned} assigned</span>
            {stats.unassigned > 0 && <span className="text-amber-600">{stats.unassigned} open</span>}
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-50 rounded-md">
            <Users className="w-3.5 h-3.5 text-gray-500" />
            <span className="font-medium">{photographers.length}</span>
            <span className="text-emerald-600">{stats.activePhotographers} active</span>
            {stats.idle > 0 && <span className="text-gray-400">{stats.idle} idle</span>}
          </div>
          {stats.totalConflicts > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-red-50 text-red-700 rounded-md">
              <AlertTriangle className="w-3.5 h-3.5" /> {stats.totalConflicts} conflicts
            </div>
          )}
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-50 rounded-md">
            Coverage: <span className={stats.coverage === 100 ? 'text-emerald-600 font-bold' : stats.coverage >= 70 ? 'text-amber-600' : 'text-red-600'}>{stats.coverage}%</span>
          </div>
        </div>
      </div>

      {/* TIMELINE VIEW */}
      {viewMode === 'timeline' && (
        <div className="flex-1 overflow-auto p-4 space-y-1">
          {/* Time axis */}
          <div className="flex">
            <div className="w-48 flex-shrink-0" />
            <div className="flex-1 relative h-6 border-b border-gray-200">
              {hourMarkers.map((m, i) => (
                <div key={i} className="absolute text-[10px] text-gray-400 font-medium" style={{ left: `${m.percent}%`, transform: 'translateX(-50%)' }}>{m.time}</div>
              ))}
            </div>
          </div>

          {/* Photographer rows */}
          {photographers.map((photographer, pIdx) => {
            const assignments = getPhotographerAssignments(photographer.id);
            const totalHours = calculateTotalHours(assignments, photographer.id);
            const conflicts = detectTimingConflict(assignments, photographer.id);
            const needsBreak = parseFloat(totalHours) > 6;
            const color = COLORS[pIdx % COLORS.length];

            return (
              <div key={photographer.id} className="flex items-stretch bg-white rounded-lg shadow-sm border transition-all"
                onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={(e) => handlePhotographerDrop(e, photographer.id)}>
                {/* Info column */}
                <div className="w-48 flex-shrink-0 p-3 border-r flex flex-col justify-center">
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${color.bg}`} />
                    <span className="font-semibold text-sm text-gray-900 truncate">{photographer.name}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge variant={needsBreak ? 'destructive' : 'secondary'} className="text-[10px] px-1.5 py-0">{totalHours}h</Badge>
                    <span className="text-[10px] text-gray-400">{assignments.length} spots</span>
                    {conflicts.length > 0 && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
                  </div>
                </div>

                {/* Timeline */}
                <div className="flex-1 relative h-16 bg-gray-50 overflow-hidden">
                  {hourMarkers.map((m, i) => <div key={i} className="absolute top-0 bottom-0 border-l border-gray-100" style={{ left: `${m.percent}%` }} />)}

                  {assignments.map((spot, idx) => {
                    const times = getPhotographerTime(spot, photographer.id);
                    const arriveMin = timeToMinutes(times.arrive_time);
                    const leaveMin = timeToMinutes(times.leave_time);
                    if (arriveMin === null || leaveMin === null) return null;

                    const left = minuteToPercent(arriveMin);
                    const width = minuteToPercent(leaveMin) - left;
                    const nextSpot = assignments[idx + 1];
                    const nextTimes = nextSpot ? getPhotographerTime(nextSpot, photographer.id) : null;
                    const nextArriveMin = nextTimes ? timeToMinutes(nextTimes.arrive_time) : null;
                    const travel = nextSpot ? calculateTravelTime(spot, nextSpot) : null;

                    const isEditing = editingTime?.spotId === spot.id && editingTime?.photographerId === photographer.id;

                    return (
                      <div key={spot.id}>
                        <div
                          className={`absolute top-2 h-12 rounded-md text-white text-[10px] font-medium flex items-center px-2 gap-1 shadow-sm hover:brightness-110 transition-all cursor-pointer group ${
                            spot.is_coverage
                              ? 'bg-amber-500 ring-2 ring-amber-300 ring-offset-1'
                              : color.bg
                          }`}
                          style={{ left: `${left}%`, width: `${Math.max(width, 3)}%`, minWidth: '40px' }}
                          title={
                            spot.is_coverage
                              ? `🔁 Break coverage: ${spot.name}\n${times.arrive_time} - ${times.leave_time}\n${
                                  spot.coverage_for_photographer_id
                                    ? `Covering for ${photographers.find((p) => p.id === spot.coverage_for_photographer_id)?.name || '—'}`
                                    : 'Covering the break'
                                }${spot.coverage_note ? `\n${spot.coverage_note}` : ''}`
                              : `${spot.name}\n${times.arrive_time} - ${times.leave_time}\nClick to show on map`
                          }
                          onClick={() => onNavigateToSpot?.({ ...spot, id: spot.original_spot_id || spot.id })}
                        >
                          {spot.is_coverage && <span className="text-[9px]">🔁</span>}
                          <span className="truncate flex-1">{spot.name}</span>
                          <button
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-white/20 rounded"
                            onClick={(e) => {
                              e.stopPropagation();
                              const targetSpot = spot.is_coverage
                                ? { ...spot, id: spot.original_spot_id }
                                : spot;
                              if (onEditSpot) onEditSpot(targetSpot);
                              else if (!spot.is_coverage) setEditingTime({ spotId: spot.id, photographerId: photographer.id, arrive_time: times.arrive_time || '', leave_time: times.leave_time || '' });
                            }}
                            title={spot.is_coverage ? 'Edit spot (with coverage)' : 'Edit spot'}
                          >
                            <Pencil className="w-2.5 h-2.5" />
                          </button>
                        </div>

                        {/* Inline time editor */}
                        {isEditing && (
                          <div className="absolute top-16 z-20 bg-white border shadow-lg rounded-lg p-2 flex items-center gap-2" style={{ left: `${left}%` }}>
                            <input type="time" className="text-xs border rounded px-1.5 py-1" value={editingTime.arrive_time}
                              onChange={(e) => setEditingTime({ ...editingTime, arrive_time: e.target.value })} />
                            <span className="text-gray-400 text-xs">to</span>
                            <input type="time" className="text-xs border rounded px-1.5 py-1" value={editingTime.leave_time}
                              onChange={(e) => setEditingTime({ ...editingTime, leave_time: e.target.value })} />
                            <Button size="sm" className="h-7 text-xs px-2" onClick={() => updatePhotographerTimes(spot.id, photographer.id, editingTime.arrive_time, editingTime.leave_time)}>Save</Button>
                            <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={() => setEditingTime(null)}>Cancel</Button>
                          </div>
                        )}

                        {/* Travel connector */}
                        {travel && nextArriveMin && (
                          <div className="absolute top-[26px] h-0 border-t-2 border-dashed border-gray-300"
                            style={{ left: `${minuteToPercent(leaveMin)}%`, width: `${minuteToPercent(nextArriveMin) - minuteToPercent(leaveMin)}%` }}
                            title={`${travel.distance}km, ~${travel.travelTime}min`} />
                        )}
                      </div>
                    );
                  })}

                  {assignments.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-300">Drop spots here</div>
                  )}
                </div>
              </div>
            );
          })}

          {/* Unassigned pool */}
          {unassignedSpots.length > 0 && (
            <div className="mt-4 bg-white rounded-lg shadow-sm border p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-amber-500" />
                Unassigned Spots ({unassignedSpots.length})
                <span className="text-xs font-normal text-gray-400">Drag onto a photographer row</span>
              </h3>
              <div className="flex flex-wrap gap-2">
                {unassignedSpots.map(spot => (
                  <div key={spot.id} draggable onDragStart={(e) => handleSpotDragStart(e, spot)}
                    className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg cursor-grab active:cursor-grabbing hover:shadow-md transition-all text-xs group"
                  >
                    <Grip className="w-3 h-3 text-amber-400" />
                    <span className="font-medium text-gray-800">{spot.name}</span>
                    {spot.arrive_time && <span className="text-gray-500">{spot.arrive_time} - {spot.leave_time || '??'}</span>}
                    {spot.lat && (
                      <button onClick={() => onNavigateToSpot?.(spot)} className="opacity-0 group-hover:opacity-100 transition-opacity" title="Show on map">
                        <Navigation className="w-3 h-3 text-blue-500" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* GRID VIEW */}
      {viewMode === 'grid' && (
        <div className="flex-1 flex gap-4 p-4 overflow-hidden">
          {/* Spots */}
          <div className="w-1/3 flex flex-col bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-blue-600 text-white px-4 py-3 font-semibold flex items-center gap-2">
              <MapPin className="w-5 h-5" /> Spots ({spots.length})
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {spots.map(spot => {
                const isSelected = selectedSpot?.id === spot.id;
                const assignedCount = spot.assigned_photographers?.length || 0;
                return (
                  <Card key={spot.id} draggable onDragStart={(e) => handleSpotDragStart(e, spot)}
                    className={`p-4 cursor-grab active:cursor-grabbing transition-all hover:shadow-md ${isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
                    onClick={() => setSelectedSpot(spot)}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-1.5">
                        <Grip className="w-3 h-3 text-gray-300" />
                        <h3 className="font-semibold text-gray-900">{spot.name}</h3>
                      </div>
                      <Badge variant={assignedCount > 0 ? 'default' : 'secondary'}>{assignedCount}/{spot.max_photographers || 1}</Badge>
                    </div>
                    {spot.arrive_time && <p className="flex items-center gap-1 text-xs text-gray-600"><Clock className="w-3 h-3" />{spot.arrive_time} - {spot.leave_time || '??'}</p>}
                    {spot.lat && (
                      <button onClick={(e) => { e.stopPropagation(); onNavigateToSpot?.(spot); }}
                        className="mt-1 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
                        <Navigation className="w-3 h-3" /> Show on map
                      </button>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Spot Details */}
          <div className="w-1/3 flex flex-col bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-emerald-600 text-white px-4 py-3 font-semibold">
              {selectedSpot ? selectedSpot.name : 'Select a Spot'}
            </div>
            {selectedSpot ? (
              <div className="flex-1 overflow-y-auto p-4">
                {/* Timing */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2"><Clock className="w-4 h-4" /> Default Timing</h3>
                  <div className="text-sm space-y-1">
                    <p><span className="font-medium">Arrive:</span> {selectedSpot.arrive_time || 'Not set'}</p>
                    <p><span className="font-medium">Leave:</span> {selectedSpot.leave_time || 'Not set'}</p>
                    {selectedSpot.first_athlete_time && <p><span className="font-medium">First Athlete:</span> {selectedSpot.first_athlete_time}</p>}
                  </div>
                </div>

                {/* Per-photographer times */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Assigned ({selectedSpot.assigned_photographers?.length || 0}/{selectedSpot.max_photographers || 1})
                  </h3>
                  {selectedSpot.assigned_photographers?.length > 0 ? (
                    <div className="space-y-3">
                      {selectedSpot.assigned_photographers.map(photogId => {
                        const photog = photographers.find(p => p.id === photogId);
                        if (!photog) return null;
                        const times = getPhotographerTime(selectedSpot, photogId);
                        const isEditing = editingTime?.spotId === selectedSpot.id && editingTime?.photographerId === photogId;

                        return (
                          <div key={photogId} className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium">{photog.name}</span>
                              <Button size="sm" variant="ghost" onClick={() => unassignPhotographer(selectedSpot.id, photogId)} className="text-red-600 hover:bg-red-50 h-6 w-6 p-0">
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                            {isEditing ? (
                              <div className="flex items-center gap-2">
                                <input type="time" className="text-xs border rounded px-2 py-1" value={editingTime.arrive_time}
                                  onChange={(e) => setEditingTime({ ...editingTime, arrive_time: e.target.value })} />
                                <span className="text-xs text-gray-400">to</span>
                                <input type="time" className="text-xs border rounded px-2 py-1" value={editingTime.leave_time}
                                  onChange={(e) => setEditingTime({ ...editingTime, leave_time: e.target.value })} />
                                <Button size="sm" className="h-7 text-xs" onClick={() => updatePhotographerTimes(selectedSpot.id, photogId, editingTime.arrive_time, editingTime.leave_time)}>Save</Button>
                                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingTime(null)}>Cancel</Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-xs text-gray-600">
                                <Clock className="w-3 h-3" />
                                <span>{times.arrive_time || '??:??'} - {times.leave_time || '??:??'}</span>
                                <button onClick={() => setEditingTime({ spotId: selectedSpot.id, photographerId: photogId, arrive_time: times.arrive_time || '', leave_time: times.leave_time || '' })}
                                  className="text-blue-500 hover:text-blue-700"><Pencil className="w-3 h-3" /></button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No photographers assigned</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-400"><p>Select a spot</p></div>
            )}
          </div>

          {/* Photographers */}
          <div className="w-1/3 flex flex-col bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-purple-600 text-white px-4 py-3 font-semibold flex items-center gap-2">
              <Users className="w-5 h-5" /> Photographers ({photographers.length})
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {photographers.map(photographer => {
                const assignments = getPhotographerAssignments(photographer.id);
                const totalHours = calculateTotalHours(assignments, photographer.id);
                const conflicts = detectTimingConflict(assignments, photographer.id);
                const needsBreak = parseFloat(totalHours) > 6;

                return (
                  <Card key={photographer.id} className={`p-4 transition-all ${needsBreak ? 'border-orange-300 bg-orange-50' : ''}`}
                    onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={(e) => handlePhotographerDrop(e, photographer.id)}>
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-gray-900">{photographer.name}</h3>
                      <Badge variant={needsBreak ? 'destructive' : 'default'}>{totalHours}h</Badge>
                    </div>
                    {needsBreak && <p className="text-xs text-orange-700 mb-2">Warning: >6h, break needed</p>}

                    {assignments.length > 0 ? (
                      <div className="space-y-2">
                        {assignments.map((spot, idx) => {
                          const times = getPhotographerTime(spot, photographer.id);
                          const nextSpot = assignments[idx + 1];
                          const travel = nextSpot ? calculateTravelTime(spot, nextSpot) : null;
                          return (
                            <div key={spot.id}>
                              <div className="bg-white border rounded p-2 text-xs cursor-pointer hover:bg-blue-50 transition-colors"
                                onClick={() => onNavigateToSpot?.(spot)}>
                                <div className="flex justify-between">
                                  <span className="font-medium text-gray-900">{spot.name}</span>
                                  <Navigation className="w-3 h-3 text-blue-400" />
                                </div>
                                <p className="text-gray-600">{times.arrive_time || '??:??'} - {times.leave_time || '??:??'}</p>
                              </div>
                              {travel && (
                                <div className="flex items-center gap-2 my-1 text-xs text-gray-500 ml-2">
                                  <Car className="w-3 h-3" /> {travel.distance}km, {travel.travelTime}min
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {conflicts.length > 0 && (
                          <div className="bg-red-50 border border-red-200 rounded p-2">
                            <p className="text-xs font-semibold text-red-900 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Conflicts:</p>
                            {conflicts.map((c, i) => <p key={i} className="text-xs text-red-800">{c.from} to {c.to}: need {c.required}min, have {c.actual}min</p>)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500">No assignments, drop a spot here</p>
                    )}

                    {selectedSpot && (
                      <Button size="sm" className="w-full mt-3" onClick={() => assignPhotographer(selectedSpot.id, photographer.id)}
                        disabled={selectedSpot.assigned_photographers?.includes(photographer.id)}>
                        {selectedSpot.assigned_photographers?.includes(photographer.id) ? 'Assigned' : 'Assign to Selected Spot'}
                      </Button>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
